import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from './App';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

const PaymentComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  const shopId = searchParams.get('shopId');

  useEffect(() => {
    const activateShop = async () => {
      if (!shopId) {
        setError('Missing shop information');
        setStatus('error');
        return;
      }

      try {
        // Get shop document
        const shopRef = doc(db, 'shops', shopId);
        const shopDoc = await getDoc(shopRef);

        if (!shopDoc.exists()) {
          setError('Shop not found');
          setStatus('error');
          return;
        }

        // Update shop status to active
        // The webhook will also update this, but we do it here for immediate feedback
        await updateDoc(shopRef, {
          status: 'active',
          subscriptionStatus: 'active',
          updatedAt: serverTimestamp()
        });

        setStatus('success');

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);

      } catch (err) {
        console.error('Error activating shop:', err);
        setError(err.message);
        setStatus('error');
      }
    };

    activateShop();
  }, [shopId, navigate]);

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-800 rounded-2xl p-8 text-center">
          {status === 'processing' && (
            <>
              <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-xl font-semibold text-white mb-2">Processing Payment...</h2>
              <p className="text-slate-400">Please wait while we activate your account.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Payment Successful!</h2>
              <p className="text-slate-400 mb-6">Your account is now active. Redirecting to dashboard...</p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition"
              >
                Go to Dashboard Now
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Something Went Wrong</h2>
              <p className="text-slate-400 mb-2">{error || 'There was an issue processing your payment.'}</p>
              <p className="text-slate-500 text-sm mb-6">If you were charged, please contact support.</p>
              <div className="flex space-x-3 justify-center">
                <button
                  onClick={() => navigate('/signup')}
                  className="px-6 py-3 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition"
                >
                  Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentComplete;
